import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import 'ui/FFDropdown/style.scss';
import cn from "classnames";
import {useMobile} from "utils";
import {ChevronDownSmallIcon} from 'icons/svg';
import {useFloatingPanel, type PanelSide} from "hooks/useFloatingPanel";

// Global counter for unique dropdown IDs
let dropdownIdCounter = 0;

// Track currently open dropdown to close others
let currentlyOpenDropdownId: string | null = null;
const dropdownCloseCallbacks = new Map<string, () => void>();

export const FFDropdown = ({children, className, top = true, ref, ...props}:
                               { top?: boolean, children: React.ReactNode, ref?: React.Ref<HTMLDivElement> } & React.HTMLAttributes<HTMLDivElement>) => {
    return <div ref={ref} className={cn('ff-dropdown-menu', className, {
        "ff-dropdown--top": top,
        "ff-dropdown--bottom": !top
    })} {...props}>
        {children}
    </div>
}

interface FFDropdownComponentProps {
    children: React.ReactNode;
    label: React.ReactNode;
    className?: string;
    triggerClassName?: string;
    dropdownClassName?: string;
    closeOnClick?: boolean;
    /** Replaces the default chevron. */
    arrow?: React.ReactNode;
    /** What opens the menu on a pointer device: `hover` (the default) or a click, as on a phone. */
    trigger?: 'hover' | 'click';
    /** Nothing opens it, and the keyboard skips it (the host dims it). */
    disabled?: boolean;
    /** How long a hover menu outlives the pointer leaving it, in ms. */
    hideDelay?: number;
    /** The side the menu opens on when it fits; the other one takes over when it does not. */
    side?: PanelSide;
    /** The trigger edge the menu lines up with, before it is shifted to stay on screen. */
    align?: 'start' | 'end';
}

export const FFDropdownComponent = ({
    children,
    label,
    className,
    triggerClassName,
    dropdownClassName,
    closeOnClick = true,
    arrow,
    trigger = 'hover',
    disabled = false,
    hideDelay = 200,
    side = 'bottom',
    align = 'start',
}: FFDropdownComponentProps) => {
    const [open, setOpen] = useState(false);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const triggerRef = useRef<HTMLDivElement | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);
    // Beside its trigger, not portalled: the outside-click check is `containerRef.contains`, and the
    // panel keeps the host's stacking context.
    const {style: placement, side: landed} = useFloatingPanel(open, triggerRef, panelRef, {side, align, strategy: 'absolute'});
    const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const dropdownId = useMemo(() => `ff-dropdown-${++dropdownIdCounter}`, []);

    const isMobile = useMobile();
    // A click-opened menu stays until a pick or an outside click — the pointer leaving it is not a close.
    const hoverable = trigger === 'hover' && !isMobile;

    // Close this dropdown
    const closeDropdown = useCallback(() => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
        setOpen(false);
        if (currentlyOpenDropdownId === dropdownId) {
            currentlyOpenDropdownId = null;
        }
    }, [dropdownId]);

    // Register close callback
    useEffect(() => {
        dropdownCloseCallbacks.set(dropdownId, closeDropdown);
        return () => {
            dropdownCloseCallbacks.delete(dropdownId);
            if (currentlyOpenDropdownId === dropdownId) {
                currentlyOpenDropdownId = null;
            }
        };
    }, [dropdownId, closeDropdown]);

    const handleOpen = useCallback(() => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
        // Close any other open dropdown
        if (currentlyOpenDropdownId && currentlyOpenDropdownId !== dropdownId) {
            const closeOther = dropdownCloseCallbacks.get(currentlyOpenDropdownId);
            if (closeOther) {
                closeOther();
            }
        }
        currentlyOpenDropdownId = dropdownId;
        setOpen(true);
    }, [dropdownId]);

    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current!.contains(event.target as Node)) {
                closeDropdown();
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => {
            document.removeEventListener('mousedown', handleClick);
        };
    }, [closeDropdown]);

    const toggle = () => {
        if (disabled) return;
        if (open) {
            setOpen(false);
        } else {
            handleOpen();
        }
    };

    const handleClose = useCallback(() => {
        hideTimeoutRef.current = setTimeout(() => closeDropdown(), hideDelay);
    }, [closeDropdown, hideDelay]);

    return (
        <div className={cn('ff-dropdown__container', className)} ref={containerRef}
             onMouseLeave={hoverable ? handleClose : undefined}
        >
            <div
                ref={triggerRef}
                className={cn('ff-dropdown__trigger', triggerClassName, {"open": open})}
                role="button"
                tabIndex={disabled ? undefined : 0}
                aria-disabled={disabled || undefined}
                aria-expanded={open}
                onMouseEnter={hoverable && !disabled ? handleOpen : undefined}
                onClick={toggle}
                onKeyDown={e => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    toggle();
                }}
            >
                {label} <span className="arrow">{arrow ?? <ChevronDownSmallIcon/>}</span>
            </div>
            {open && (
                <div
                    ref={panelRef}
                    className={cn('ff-dropdown__panel', `ff-dropdown--${landed}`, dropdownClassName)}
                    style={placement}
                    onClick={closeOnClick ? () => setOpen(false) : undefined}
                    onMouseEnter={handleOpen}
                >
                    {children}
                </div>
            )}
        </div>
    );
}